import {useDerivedProduct as useDerivedProductBase} from '@salesforce/retail-react-app/app/hooks/use-derived-product';
import {
  isSimpleProduct as checkIsSimpleProduct,
  isComplexProduct as checkIsComplexProduct,
  hasConfigurableOptions as checkHasConfigurableOptions,
  detectSimpleProductType,
  SIMPLE_PRODUCT_TYPE,
} from '@amblique/king-living/app/utils/product-utils';

/**
 * @override
 * - Simple product: c_isGuidedPDP = false OR no productLinks
 * - Complex product: c_isGuidedPDP = true OR has productLinks
 * - Configurable options: simple product with c_optionsGroupingUIProcessed or c_coverGroupsData
 * - Fix isOutOfStock false-positive for type.item products
 * - Detect simple product type and finished good state
 *
 * @param {object} product
 * @param {boolean} isProductPartOfSet
 * @param {boolean} isProductPartOfBundle
 * @returns {object}
 */
export const useDerivedProduct = (
  product,
  isProductPartOfSet = false,
  isProductPartOfBundle = false
) => {
  const derivedProduct = useDerivedProductBase(product, isProductPartOfSet, isProductPartOfBundle);

  const isSimpleProduct = checkIsSimpleProduct(product);
  const isComplexProduct = checkIsComplexProduct(product);
  const simpleProductType = detectSimpleProductType(product);
  // Single source of truth — the enum branch in `detectSimpleProductType` runs
  // the same `isFinishedGood(product)` check, so deriving here keeps the flag
  // and the enum in lockstep (no risk of one returning true while the other
  // routes through COLOR_SIZE_OPTIONS / MULTIPLE_CUSTOMIZE_STEPS).
  const isFinishedGood = simpleProductType === SIMPLE_PRODUCT_TYPE.FINISHED_GOOD;
  // Finished Goods opt out of the configurable options flow — their options are
  // pre-selected and non-interactive. Without this guard, a Finished Good whose
  // c_optionsGroupingUI is populated (inherited from the master) would be routed
  // into ComplexAddToCartButton with isDisabled=true (KINDW-1557).
  const hasConfigurableOptions = isFinishedGood ? false : checkHasConfigurableOptions(product);

  // Fix OOS detection for type.item products using inventory.orderable
  const isStandardProduct = !!product?.type?.item;

  const isOutOfStock = isStandardProduct ?
    product?.inventory?.orderable === false :
    derivedProduct.isOutOfStock;

  const isFiniteStock = isStandardProduct ? derivedProduct.stockLevel > 0 : true;

  const showInventoryMessage = isStandardProduct ?
    isOutOfStock || (isFiniteStock && derivedProduct.unfulfillable) :
    derivedProduct.showInventoryMessage;

  return {
    ...derivedProduct,
    isOutOfStock,
    showInventoryMessage,
    isSimpleProduct,
    isComplexProduct,
    hasConfigurableOptions,
    isFinishedGood,
    simpleProductType,
  };
};
